<template>
  <div class="locked-page">
    <div class="language-switcher">
      <language-switcher />
    </div>
    <div class="locked-container">
      <!-- Lock Icon -->
      <div class="lock-icon-wrapper">
        <el-icon :size="64" class="lock-icon">
          <Lock />
        </el-icon>
      </div>

      <!-- Title -->
      <h1 class="locked-title">{{ $t('locked.title') }}</h1>
      
      <!-- Message -->
      <p class="locked-message">{{ $t('locked.message') }}</p>

      <!-- Contact Info Card -->
      <div class="contact-card">
        <div class="contact-card-title">
          <el-icon><Service /></el-icon>
          {{ $t('locked.contactUs') }}
        </div>
        
        <div class="contact-info-list">
          <div class="contact-info-item">
            <div class="contact-icon phone">
              <el-icon><Phone /></el-icon>
            </div>
            <div class="contact-detail">
              <span class="contact-label">{{ $t('locked.phone') }}</span>
              <a :href="'tel:' + contactInfo.phone" class="contact-value">{{ contactInfo.phone }}</a>
            </div>
          </div>
          
          <div class="contact-info-item">
            <div class="contact-icon email">
              <el-icon><Message /></el-icon>
            </div>
            <div class="contact-detail">
              <span class="contact-label">{{ $t('locked.email') }}</span>
              <a :href="'mailto:' + contactInfo.email" class="contact-value">{{ contactInfo.email }}</a>
            </div>
          </div>
          
          <div class="contact-info-item">
            <div class="contact-icon web">
              <el-icon><Link /></el-icon>
            </div>
            <div class="contact-detail">
              <span class="contact-label">{{ $t('locked.website') }}</span>
              <a :href="contactInfo.website" target="_blank" rel="noopener noreferrer" class="contact-value">
                {{ contactInfo.website }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <el-button 
          type="primary" 
          class="check-status-btn"
          :loading="checkingStatus"
          @click="handleCheckStatus"
        >
          <el-icon><RefreshRight /></el-icon>
          {{ $t('locked.checkStatus') }}
        </el-button>
        
        <el-button 
          type="info" 
          class="logout-btn"
          @click="handleLogout"
        >
          <el-icon><SwitchButton /></el-icon>
          {{ $t('locked.logout') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Lock, Phone, Message, Link, Service, SwitchButton, RefreshRight } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { getUserInfo } from '@/api/auth'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'

const router = useRouter()
const userStore = useUserStore()
const { t } = useI18n()

const checkingStatus = ref(false)

const contactInfo = {
  phone: '18690320261',
  email: 'support@yolal.cn',
  website: 'https://www.yolal.cn'
}

const handleCheckStatus = async () => {
  checkingStatus.value = true
  
  try {
    await getUserInfo()
    
    ElMessage.success(t('locked.statusUnlocked'))
    localStorage.removeItem('software-locked')
    
    setTimeout(() => {
      router.replace('/dashboard')
    }, 500)
  } catch (error) {
    const errorMsg = error.message || ''
    if (errorMsg.includes('SOFTWARE_PAYMENT_EXPIRED') || errorMsg.includes('订阅已过期')) {
      ElMessage.error(t('locked.statusStillLocked'))
    } else {
      ElMessage.error(t('locked.checkStatusFailed'))
    }
  } finally {
    checkingStatus.value = false
  }
}

const handleLogout = async () => {
  userStore.clearSession()
  localStorage.removeItem('software-locked')
  router.replace('/login')
}
</script>

<style scoped lang="scss">
.locked-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding: 24px;
  position: relative;
}

.language-switcher {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
}

.locked-container {
  background: #fff;
  border-radius: 24px;
  padding: 48px 40px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);

  @media (max-width: 480px) {
    padding: 36px 24px;
    border-radius: 20px;
  }
}

.lock-icon-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f56c6c20 0%, #f56c6c10 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  .lock-icon {
    color: #f56c6c;
  }
}

.locked-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px;
  letter-spacing: -0.3px;

  @media (max-width: 480px) {
    font-size: 20px;
  }
}

.locked-message {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 32px;
}

.contact-card {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: left;

  .contact-card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 20px;

    .el-icon {
      color: #409eff;
      font-size: 18px;
    }
  }

  .contact-info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .contact-info-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px;
    background: #fff;
    border-radius: 12px;
    transition: all 0.2s ease;

    &:hover {
      background: #f0f2f5;
    }

    .contact-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 18px;
      color: #fff;

      &.phone {
        background: linear-gradient(135deg, #67c23a, #85ce61);
      }

      &.email {
        background: linear-gradient(135deg, #409eff, #66b1ff);
      }

      &.web {
        background: linear-gradient(135deg, #e6a23c, #ebb563);
      }
    }

    .contact-detail {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;

      .contact-label {
        font-size: 12px;
        color: #909399;
        font-weight: 500;
      }

      .contact-value {
        font-size: 14px;
        color: #303133;
        font-weight: 600;
        text-decoration: none;
        word-break: break-all;

        &:hover {
          color: #409eff;
        }
      }
    }
  }
}

.action-buttons {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.check-status-btn {
  flex: 1;
  height: 48px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.logout-btn {
  flex: 1;
  height: 48px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
</style>